import { useEffect, useState } from 'react';
import { ErrorMessage, useField, useFormikContext } from 'formik';
import { DocumentDownload, MessageQuestion, MessageTick } from 'iconsax-react';
import { Loader2 } from 'lucide-react';
import { mixed, object, string } from 'yup';

import { Button } from '@design-system/button';
import { CustomErrorMessage } from '@design-system/error-message';
import FileUpload from '@design-system/molecules/file-upload';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@design-system/tabs';
import { downloadImportTemplate } from '@shared/services/download-template';
import { showToast } from '@shared/utils/helpers/show-toast';

import {
  FAQ_MAX_LENGTH,
  importKnowledgeFaqs,
  knowledgeFaqsApi,
  type KnowledgeFaq,
} from '../services/knowledge-content-api';
import { ContentFormDialog } from './content-form-dialog';

type Tab = 'single' | 'import';

type Values = {
  question: string;
  answer: string;
  file: File | null;
};

const singleSchema = object({
  question: string()
    .trim()
    .required('Question is required')
    .max(FAQ_MAX_LENGTH, `At most ${FAQ_MAX_LENGTH} characters`),
  answer: string()
    .trim()
    .required('Answer is required')
    .max(FAQ_MAX_LENGTH, `At most ${FAQ_MAX_LENGTH} characters`),
});

const importSchema = object({
  file: mixed<File>()
    .required('File is required')
    .test(
      'file-required',
      'File is required',
      (value) => value instanceof File
    ),
});

/** One auto-growing line of the question/answer pair, with its icon. */
function PairField({
  name,
  label,
  icon,
  className,
}: {
  name: 'question' | 'answer';
  label: string;
  icon: React.ReactNode;
  className?: string;
}) {
  const [field, meta] = useField<string>(name);
  return (
    <div className={className}>
      <label className='flex items-start gap-3 px-4 py-3'>
        <span
          aria-hidden='true'
          className='mt-0.5 shrink-0 text-gray-700'
        >
          {icon}
        </span>
        <textarea
          {...field}
          aria-label={label}
          placeholder={label}
          rows={1}
          aria-invalid={Boolean(meta.error && meta.touched)}
          className='field-sizing-content max-h-60 min-h-6 w-full resize-none bg-transparent text-[15px] text-gray-800 outline-none placeholder:text-gray-400'
        />
      </label>
      <ErrorMessage name={name}>
        {(msg) => (
          <CustomErrorMessage className='mx-4 mb-2 mt-0'>
            {msg}
          </CustomErrorMessage>
        )}
      </ErrorMessage>
    </div>
  );
}

function SingleFaqFields() {
  return (
    <div className='overflow-hidden rounded-xl border border-pale-border'>
      <PairField
        name='question'
        label='Add Question'
        icon={<MessageQuestion size={22} />}
        className='border-b border-pale-border bg-primary-30'
      />
      <PairField
        name='answer'
        label='Add Answer'
        icon={<MessageTick size={22} />}
      />
    </div>
  );
}

function ImportFaqsFields() {
  const { values, setFieldValue } = useFormikContext<Values>();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadImportTemplate(
        'knowledge-faqs/download-import-template/',
        'faqs_import_template'
      );
      showToast({
        title: 'Template downloaded successfully',
        variant: 'success',
      });
    } catch {
      showToast({
        title: 'Failed to download template. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between'>
        <label className='text-sm font-medium text-gray-800'>
          Upload Multiple FAQs
        </label>
        <Button
          type='button'
          className='h-fit w-fit px-1 py-0.5'
          variant='ghost'
          onClick={handleDownloadTemplate}
          disabled={isDownloading}
        >
          <span className='-mr-1 text-sm font-medium text-primary-1000 underline'>
            DOWNLOAD TEMPLATE
          </span>
          {isDownloading ? (
            <Loader2 className='h-4! w-4! animate-spin text-primary-1000' />
          ) : (
            <DocumentDownload className='h-4! w-4! text-primary-1000' />
          )}
        </Button>
      </div>
      <FileUpload
        file={values.file}
        name='file'
        onChange={(file) => setFieldValue('file', file)}
        allowedFormats='.xlsx, .xls, .xlsm, .csv'
        maxSize='800x400px'
      />
      <ErrorMessage
        name='file'
        component={CustomErrorMessage}
      />
    </div>
  );
}

export function FaqDialog({
  kbaseId,
  faq,
  open,
  onOpenChange,
}: {
  kbaseId: number;
  /** null adds a new FAQ. */
  faq: KnowledgeFaq | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [tab, setTab] = useState<Tab>('single');
  const create = knowledgeFaqsApi.useCreate();
  const update = knowledgeFaqsApi.useUpdate();
  const importFaqs = importKnowledgeFaqs.useCreate();
  const editing = faq !== null;

  useEffect(() => {
    if (!open) setTab('single');
  }, [open]);

  return (
    <ContentFormDialog<Values>
      open={open}
      onOpenChange={onOpenChange}
      title={faq ? 'Edit FAQ' : 'Add FAQ'}
      subtitle='Train Your Model On FAQs'
      editing={editing}
      formKey={editing ? 'edit' : tab}
      bodyClassName='p-0!'
      isPending={create.isPending || update.isPending || importFaqs.isPending}
      initialValues={{
        question: faq?.question ?? '',
        answer: faq?.answer ?? '',
        file: null,
      }}
      validationSchema={
        editing || tab === 'single' ? singleSchema : importSchema
      }
      onSubmit={(values) => {
        if (!editing && tab === 'import') {
          return importFaqs.mutateAsync({
            kbase: kbaseId,
            file: values.file as File,
          });
        }
        const payload = {
          question: values.question.trim(),
          answer: values.answer.trim(),
        };
        return faq
          ? update.mutateAsync({ id: faq.id, ...payload })
          : create.mutateAsync({ kbase: kbaseId, ...payload });
      }}
    >
      {editing ? (
        <div className='p-4'>
          <SingleFaqFields />
        </div>
      ) : (
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as Tab)}
        >
          <TabsList className='h-14 w-full pb-0'>
            <TabsTrigger
              className='border-0'
              value='single'
            >
              Single FAQ
            </TabsTrigger>
            <TabsTrigger
              className='border-0'
              value='import'
            >
              Import FAQs
            </TabsTrigger>
          </TabsList>
          <TabsContent
            value='single'
            className='mt-0 p-4'
          >
            <SingleFaqFields />
          </TabsContent>
          <TabsContent
            value='import'
            className='mt-0 p-4'
          >
            <ImportFaqsFields />
          </TabsContent>
        </Tabs>
      )}
    </ContentFormDialog>
  );
}
